import { z } from 'zod/v4'

import { isoDateQueryParamSchema } from '../../validation/date-query-param.js'
import { defineScreen, type ScreenData, type ScreenQuery } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'

/*
 * «Usuarios»: the counts strip, one page of users and the super admins in one answer. Each schema mirrors the
 * interface the services return (`SystemUserResponse`, `UserStatsResponse`, `SuperAdmin`); the type assertions of
 * `shared-users-screen.type-assertions.ts` tie them in both directions. No row carries a credential.
 */

const systemUserStatusSchema = z.enum(['active', 'suspended', 'pending_verification'])

export const systemUserRowSchema = z.strictObject({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  /** Text, as in `users.role`: an organisation can assign a role of its own, which a closed enum would refuse. */
  role: z.string().min(1),
  status: systemUserStatusSchema,
  tenant: z.strictObject({ id: z.string(), name: z.string() }).nullable(),
  company: z.string().nullable(),
  phone: z.string().nullable(),
  timezone: z.string().nullable(),
  emailVerified: z.boolean(),
  twoFactorEnabled: z.boolean(),
  failedLoginAttempts: z.number(),
  lockedUntil: z.string().nullable(),
  suspendedReason: z.string().nullable(),
  invitationPending: z.boolean().optional(),
  lastLoginAt: z.string().nullable(),
  createdAt: z.string(),
})

export const userStatsSchema = z.strictObject({
  total: z.number(),
  active: z.number(),
  suspended: z.number(),
  pendingVerification: z.number(),
  byRole: z.record(z.string(), z.number()),
  newThisMonth: z.number(),
  newLastMonth: z.number(),
  usersWithTwoFactor: z.number(),
  usersWithoutTwoFactor: z.number(),
  twoFactorCompliancePercentage: z.number(),
})

export const superAdminSchema = z.strictObject({
  id: z.string(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  status: z.string(),
  createdAt: z.string(),
  lastLoginAt: z.string().nullable(),
})

export const adminPaginationSchema = z.strictObject({
  page: z.number(),
  limit: z.number(),
  total: z.number(),
  totalPages: z.number(),
})

/**
 * The same limits as the route this screen replaces. The role is checked against the registry by the composition (it
 * is read from the database, a contract cannot know it): an unknown one is a 400 there.
 */
export const adminUsersQuerySchema = z.strictObject({
  page: z.coerce.number<number>().int().min(1).default(1),
  limit: z.coerce.number<number>().int().min(1).max(100).default(20),
  tenantId: z.uuid().optional(),
  role: z.string().min(1).max(100).optional(),
  status: systemUserStatusSchema.optional(),
  search: z.string().min(1).max(255).optional(),
  sortBy: z.enum(['email', 'name', 'createdAt', 'lastLoginAt']).default('createdAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
  dateFrom: isoDateQueryParamSchema.optional(),
  dateTo: isoDateQueryParamSchema.optional(),
  lastLoginFrom: isoDateQueryParamSchema.optional(),
  lastLoginTo: isoDateQueryParamSchema.optional(),
})

export const adminUsersScreen = defineScreen({
  id: 'admin.users',
  path: '/api/screens/admin/users',
  access: 'super_admin',
  query: adminUsersQuerySchema,
  // Sections run in series in the order of the keys: `page` goes first because its composition validates the query
  // (the role) before any other read.
  response: z.strictObject({
    page: screenSection(
      z.strictObject({
        items: z.array(systemUserRowSchema),
        pagination: adminPaginationSchema,
      }),
    ),
    counts: screenSection(userStatsSchema),
    superAdmins: screenSection(z.array(superAdminSchema)),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.users'],
  budget: { requests: 1, statements: 8, bytes: 60_000, p95Ms: 200 },
  summary: 'Users: counts, one page of users and the super admins',
})
export type AdminUsersQuery = ScreenQuery<typeof adminUsersScreen>
export type AdminUsersData = ScreenData<typeof adminUsersScreen>
